


import { Component, Vue, Emit, Watch } from 'vue-property-decorator';
import Preset from '../Preset/index.vue';
import { Preset as IPreset } from '../../utils/api';
import { namespace } from 'vuex-class';

const homeModule = namespace('home');

@Component({
  components: {
    Preset
  }
})
export default class PresetGallery extends Vue {
  @homeModule.State private presets!: IPreset[];
  @homeModule.State private currentPresetIndex!: number;
  @homeModule.Getter private numberOfPresets!: number;
  @homeModule.Action private setCurrentIndex!: (newIndex: number) => void;

  private presetsReference!: HTMLElement;
  private elementWidth!: number;
  private contentWidth!: number;
  private galleryWidth!: number;
  private numberOfPresetsInScreen!: number;
  private indexChangeThreshold!: number;
  private dimensionsSet = false;
  private debounceTimer!: ReturnType<typeof setTimeout>;
  private throttleTimer: ReturnType<typeof setTimeout> | null = null;
  private currentCalculatedIndex!: number;
  private mouseGalleryPosition = { left: 0, x: 0 };
  private needForRAF = true;
  private isTall = false;

  @Emit()
  private selectPreset(preset: IPreset) {
    return preset;
  }

  @Watch('currentPresetIndex')
  private watchCurrentPresetIndex(newValue: number) {
    if (newValue !== this.currentCalculatedIndex) {
      if (!this.dimensionsSet) {
        this.updateDimensions();
      }
      this.presetsReference.scrollLeft = newValue * this.elementWidth;
      this.currentCalculatedIndex = newValue;
    }
  }

  private updateCurrentIndex() {
    if (!this.dimensionsSet) {
      this.updateDimensions();
    }

    if (this.currentCalculatedIndex !== this.currentPresetIndex) {
      this.currentCalculatedIndex = this.currentPresetIndex;
      return;
    }
    const scrollLeft = this.presetsReference.scrollLeft;
    const ratio = scrollLeft / this.elementWidth;
    const modulo = Math.round(ratio + this.indexChangeThreshold);
    const scrollAmountLeft = this.contentWidth - ratio * this.elementWidth;
    let index: number;

    if (scrollAmountLeft < this.elementWidth * this.numberOfPresetsInScreen + 60) {
      index = this.numberOfPresets - 1;
    } else {
      index = modulo;
    }

    this.currentCalculatedIndex = index;

    if (index !== this.currentPresetIndex) {
      this.setCurrentIndex(index);
    }
  }

  private updateTall() {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    /**
     * Specs: For tablets everything below 760px is not tall, for desktop everything below 950px. Everything
     * narrower than 1440px is considered to be a tablet.
     */
    let tallHeight = 760;

    if (screenWidth >= 1440) {
      tallHeight = 900;
    }

    const isTallCurrent = screenHeight >= tallHeight;

    if (isTallCurrent !== this.isTall) {
      this.isTall = isTallCurrent;
      this.dimensionsSet = false;
    }
  }

  mounted() {
    window.addEventListener('resize', this.resize);
    this.presetsReference = this.$refs.presets as HTMLElement;
    this.currentCalculatedIndex = this.currentPresetIndex;
    this.updateTall();

    this.presetsReference.addEventListener('mousedown', this.mouseDownHandler);
  }

  unmounted() {
    window.removeEventListener('resize', this.resize);
    document.removeEventListener('mousemove', this.mouseMoveHandler);
    document.removeEventListener('mouseup', this.mouseUpHandler);
  }

  private mouseUpHandler(e: MouseEvent) {
    e.preventDefault();
    this.presetsReference.style.cursor = 'grab';
    this.presetsReference.style.scrollBehavior = 'smooth';
    this.presetsReference.style.removeProperty('user-select');

    document.removeEventListener('mousemove', this.mouseMoveHandler);
    document.removeEventListener('mouseup', this.mouseUpHandler);
  }

  private mouseDownHandler(e: MouseEvent) {
    e.preventDefault();

    if (!this.dimensionsSet) {
      this.updateDimensions();
    }

    // Change the cursor and prevent user from selecting the text
    this.presetsReference.style.cursor = 'grabbing';
    this.presetsReference.style.userSelect = 'none';
    this.presetsReference.style.scrollBehavior = 'initial';
    this.mouseGalleryPosition.left = this.presetsReference.scrollLeft;
    this.mouseGalleryPosition.x = e.clientX;

    document.addEventListener('mousemove', this.mouseMoveHandler);
    document.addEventListener('mouseup', this.mouseUpHandler);
  }

  private mouseMoveHandler(e: MouseEvent) {
    e.preventDefault();
    const dx = e.clientX - this.mouseGalleryPosition.x;

    if (this.needForRAF) {
      this.needForRAF = false;
      requestAnimationFrame(() => {
        this.needForRAF = true;
        this.presetsReference.scrollLeft = this.mouseGalleryPosition.left - dx;
      });
    }
  }

  private resize(): void {
    const waitingTime = 200;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // update vertical overlaps on the go
    if (this.throttleTimer === null) {
      this.throttleTimer = setTimeout(() => {
        this.updateTall();
        this.throttleTimer = null;
      }, waitingTime);
    }

    // update index at the end
    this.debounceTimer = setTimeout(() => {
      // after every resize / rotation recalculate everything
      this.dimensionsSet = false;
      this.updateTall();
      Vue.nextTick(this.updateCurrentIndex);
    }, waitingTime);
  }

  private updateDimensions(): void {
    this.galleryWidth = this.$el.clientWidth;
    this.contentWidth = this.presetsReference.scrollWidth;
    this.elementWidth = this.contentWidth / this.numberOfPresets;
    this.numberOfPresetsInScreen = this.galleryWidth / this.elementWidth;
    this.indexChangeThreshold = -0.5;
    if (this.numberOfPresetsInScreen > 1.0) {
      this.indexChangeThreshold = Math.min(this.numberOfPresetsInScreen - 1.5, 0.4);
    }
    this.dimensionsSet = true;
  }
}
